import { SessionType, TOtpConfigType } from 'data/types'
import isNil from 'lodash/isNil'
import omit from 'lodash/omit'

import { AuthActionType } from './actions'
import * as AT from './actionTypes'
import extraKYCReducer, {
  ExtraKYCQuestionsState,
  initialState as extraKYCInitialState
} from './ExtraKYCQuestions/slice'
import localSettingsReducer from './localSettings/reducers'
import secondFactorChallengeReducer, {
  initialState as secondFactorChallengeInitialState,
  SecondFactorChallengeState
} from './SecondFactorChallenge/slice'
import settingsReducer, {
  initialState as settingsInitialState,
  SettingsState
} from './Settings/slice'
import {
  AccumulatorType,
  ApiSettingsType,
  DeviceInfoType,
  LocalSettingsType,
  TiersLimitsVolumeType,
  TwoFAType,
  UserFeesType,
  UserType
} from './types'
import ukFrictionsReducer, { initialState as ukFrictionsInitialState } from './UKFrictions/slice'

export type AuthStateType = {
  aggregatedFees?: Array<AccumulatorType>
  api?: ApiSettingsType
  deviceInfo?: DeviceInfoType
  emailVerificationId?: string
  extraKYCQuestions: ExtraKYCQuestionsState
  fees?: UserFeesType
  isAuthing: boolean
  localSettings?: LocalSettingsType
  promos?: UserFeesType
  referrerUsername?: string
  secondFactorChallenge: SecondFactorChallengeState
  session?: SessionType
  settings: SettingsState
  socketAuthed?: boolean
  tOtpConfig?: TOtpConfigType
  tiersLimitsVolume?: TiersLimitsVolumeType
  twoFactorStatus?: Array<TwoFAType>
  ukFrictions: any
  user?: UserType
}

const initialState: AuthStateType = {
  extraKYCQuestions: extraKYCInitialState,
  isAuthing: false,
  secondFactorChallenge: secondFactorChallengeInitialState,
  settings: settingsInitialState,
  ukFrictions: ukFrictionsInitialState
}

const updateSettings = (state, update) => ({
  ...state,
  user: {
    ...state.user,
    settings: {
      ...state.user?.settings,
      ...update
    }
  }
})

const auth = (state: AuthStateType = initialState, action: AuthActionType): AuthStateType => {
  switch (action.type) {
    case AT.REMOVE_TOTP_CONFIG: {
      return {
        ...state,
        tOtpConfig: undefined
      }
    }

    case AT.SET_API_KEYS: {
      const newKey = (state.api && state.api.newKey) || {}
      const keyToDeactivate = (state.api && state.api.keyToDeactivate) || ''
      return {
        ...state,
        api: { keyToDeactivate, keys: action.payload.keys, newKey }
      }
    }

    case AT.SET_API_KEY_DEACTIVATE: {
      const newKey = (state.api && state.api.newKey) || {}
      const keys = (state.api && state.api.keys) || []
      return {
        ...state,
        api: { keyToDeactivate: action.payload.keyToDeactivate, keys, newKey }
      }
    }

    case AT.SET_DEVICE_INFO: {
      const { deviceInfo } = action.payload
      return {
        ...state,
        deviceInfo
      }
    }

    case AT.SET_EMAIL_VERIFICATION_ID: {
      return {
        ...state,
        emailVerificationId: action.payload.emailVerificationId
      }
    }

    case AT.SET_NEW_API_KEY_CREDENTIALS: {
      const keys = (state.api && state.api.keys) || []
      const keyToDeactivate = (state.api && state.api.keyToDeactivate) || ''
      return {
        ...state,
        api: { keyToDeactivate, keys, newKey: action.payload.credentials }
      }
    }

    case AT.SET_REFERRER_USERNAME: {
      return {
        ...state,
        referrerUsername: action.payload.referrerUsername
      }
    }

    case AT.SET_SESSION: {
      return {
        ...state,
        session: action.payload.session
      }
    }

    case AT.SET_SOCKET_AUTHED: {
      return {
        ...state,
        socketAuthed: action.payload.authenticated
      }
    }

    case AT.SET_TOTP_CONFIG: {
      return {
        ...state,
        tOtpConfig: action.payload.tOtpConfig
      }
    }

    case AT.SET_TWO_FACTOR_STATUS: {
      return {
        ...state,
        twoFactorStatus: action.payload.status
      }
    }

    case AT.SET_USER: {
      if (!isNil(action.payload.user)) {
        return {
          ...state,
          user: action.payload.user
        }
      }

      return omit(state, 'user')
    }

    case AT.SET_USER_SETTINGS: {
      return updateSettings(state, action.payload.settings)
    }

    case AT.SET_TIERS_LIMITS_VOLUME: {
      return {
        ...state,
        tiersLimitsVolume: action.payload.limits
      }
    }

    case AT.SET_USER_FEES: {
      return {
        ...state,
        fees: action.payload.fees
      }
    }
    case AT.SET_USER_AGGREGATED_FEES: {
      return {
        ...state,
        aggregatedFees: action.payload.aggregatedFees
      }
    }

    case AT.SET_USER_PROMOS: {
      return {
        ...state,
        promos: action.payload.fees
      }
    }

    case AT.SIGNOUT: {
      return {
        ...state,
        user: undefined
      }
    }

    case AT.IS_AUTHING: {
      return { ...state, isAuthing: action.payload }
    }

    default:
      return state
  }
}

export default function (state: AuthStateType = initialState, action: AuthActionType) {
  return {
    ...auth(state, action),
    extraKYCQuestions: extraKYCReducer(state.extraKYCQuestions, action),
    localSettings: localSettingsReducer(state.localSettings, action),
    secondFactorChallenge: secondFactorChallengeReducer(state.secondFactorChallenge, action),
    settings: settingsReducer(state.settings, action),
    ukFrictions: ukFrictionsReducer(state.ukFrictions, action)
  }
}
